// Коммит фоновой картинки, ВЛАДЕЛЕЦ которого — блок урока (п.24). Работает
// только тогда, когда места загрузки уже нет на экране: пока оно живо, URL
// уходит в его же черновик (см. `resolveCommit`), и незапрошенных `PUT` из-под
// автора не бывает. Живёт рядом с очередью, а не в редакторе блока, ровно
// потому, что переживает его. Правила: тело из свежего серверного блока,
// молчаливого коммита поверх несохранённой правки нет, занятый прогоном блок
// не трогаем вовсе, а любой отказ убирает за собой загруженный файл (у ролика
// убирать нечем: ручки удаления «ничейного» видео нет, см. `kind`).
import { useCallback, useEffect, useRef } from 'react'
import { useStore } from 'react-redux'
import { toast } from 'sonner'
import type { RootState } from '@/store'
import { useAppDispatch } from '@/store/hooks'
import { blocksApi } from '@/features/editor/blocksApi'
import { toBlockCreate } from '@/features/editor/lib/duplicateBlock'
import { resolveAlias } from '@/features/editor/lib/idAliases'
import { waitDraftSourceSettled } from '@/features/moderation/lib/draftSourceSettling'
import { useBlockDraftGate } from '../studio/BlockDraftContext'
import { isBlockLocked, lockReasonOf, useBlocksInRun } from '../studio/useBlocksInRun'
import { useFileUpload } from './useFileUpload'
import type { BlockUploadTarget, CommitUpload } from './uploadTask'
import { uploadCommitText, type UploadKind } from './uploadCommitText'
import { readFreshBlock } from './freshBlock'

/**
 * Строит коммит для конкретной цели. Гейты читаются РЕФОМ в момент коммита, а
 * не на старте загрузки: пока файл летел, автор мог начать правку блока, а
 * прогон — занять его.
 */
export const useBlockUploadCommit = (
  courseId: number,
  /** Что коммитим: URL картинки или Kinescope id ролика (`VideoUploadProvider`).
   *  От вида зависят тексты тостов и уборка на отказе. */
  kind: UploadKind = 'image',
): ((target: BlockUploadTarget) => CommitUpload) => {
  const store = useStore<RootState>()
  const dispatch = useAppDispatch()
  // Отказ обязан убирать за собой: только что загруженный файл ни на что не
  // ссылается, и без удаления каждый отказ протекал бы блобом в хранилище.
  // Путь по умолчанию (`onChange`) так не делает намеренно — там URL уже мог
  // уехать в форму владельца, и он вправе повторить сохранение.
  const { removeFile } = useFileUpload()
  const gate = useBlockDraftGate()
  const blocksInRun = useBlocksInRun(courseId)
  const gatesRef = useRef({ gate, blocksInRun })
  useEffect(() => {
    gatesRef.current = { gate, blocksInRun }
  })

  return useCallback(
    (target: BlockUploadTarget): CommitUpload =>
      async (url: string) => {
        // ПЕРВЫМ шагом, до алиасов: студия могла не доехать до только что заведённого
        // черновика, и тело собралось бы из живого блока. Очередь последовательная —
        // следующие задачи подождут сами.
        await waitDraftSourceSettled(store, courseId)
        const gates = gatesRef.current
        const text = uploadCommitText(kind)
        const refuse = (message: string) => {
          // Kinescope id — не файл нашего хранилища: `removeFile` по нему ушёл бы
          // в чужую ручку. Ролик остаётся в Kinescope сиротой.
          if (kind === 'image') removeFile(url)
          toast.error(message)
          return false
        }
        // Цель снята при постановке задачи, а источник чтения курса мог с тех пор
        // смениться (появился черновик, его слили): те же урок и блок теперь под
        // другими id. Иначе тело взялось бы из отставшего кеша прежнего урока.
        const aliases = store.getState().editor.idAliases
        const lessonId = resolveAlias(aliases, 'lesson', target.lessonId)
        const blockId = resolveAlias(aliases, 'block', target.blockId)
        if (isBlockLocked(gates.blocksInRun, blockId)) {
          // Проверка — не прогон: «когда прогон закончится» соврало бы.
          const review = lockReasonOf(gates.blocksInRun) === 'review'
          return refuse(review ? text.review : text.run)
        }
        if (gates.gate.blockId === blockId && gates.gate.isDirty) return refuse(text.dirty)
        // Свежий серверный блок, а не снимок на момент броска файла: `PUT`
        // блока — полная замена тела, и всё, что приехало за время загрузки
        // (правка прогона, сохранение из другой вкладки), обязано уехать назад.
        const fresh = await readFreshBlock(store.getState, dispatch, lessonId, blockId)
        if (!fresh) return refuse(text.gone)
        // `null` — места для картинки в свежем блоке нет. Отправлять
        // неизменённое тело нельзя: `PUT` прошёл бы, автор увидел бы
        // «сохранено», а картинки в блоке не было бы.
        const next = target.apply(fresh, url)
        if (!next) return refuse(text.noPlace)
        const put = dispatch(
          blocksApi.endpoints.updateBlock.initiate({
            id: blockId,
            lessonId,
            body: toBlockCreate(next, fresh.order),
          }),
        )
        try {
          await put.unwrap()
          toast.success(text.saved)
          return true
        } catch {
          return refuse(text.failed)
        } finally {
          put.reset()
        }
      },
    [store, dispatch, removeFile, courseId, kind],
  )
}
